import * as React from 'react';
import type {ChatMessage, ActionEvent} from 'src/components/agentic-ui';
import {useQueryClient, skipToken} from '@tanstack/react-query';
import {
  getMessageHistoryQueryKey,
  FULLSCREEN_MODE_KEY,
  POSITION_PERSIST_KEY,
  LAST_MODE_STORAGE_KEY,
} from 'src/utils/ask-ai';
import type {ApiPostOptions} from 'src/utils/redux-api';
import {useDispatch, useSelector} from 'react-redux';
import {useEnvironmentContext} from 'src/hooks/useEnvironmentContext';
import logger from 'src/utils/logger';
import useAgencyConfig from 'src/hooks/useAgencyConfig';
import {
  ChatPanelLexical,
  type ChatPanelHandle,
} from 'src/components/agentic-ui/chat-panel';
import {
  selectFullscreen,
  selectSideNavExpanded,
} from 'src/selectors/app-config';
import {selectIsAuthed} from 'src/selectors/accounts';
import {
  useRespondToAutoAttach,
  useRequestToAutoAttach,
  useHeaderSlot,
} from 'src/components/agentic-ui/use-auto-attach';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {classify} from '@spaced-out/ui-design-system/lib/utils/classify';
import {useApiMutation} from 'src/hooks/useApiMutation';

import {AgenticSuggestions} from 'src/components/agentic-ui/agentic-suggestions';
import {useApiQuery} from 'src/hooks/useApiQuery';
import * as reduxApiAskAi from 'src/utils/redux-api-ask-ai';
import css from './ask-ai.css';
import {
  registerChatPanel,
  useAskAIProduct,
  useAskAIThreadId,
  useShouldFetchStarterPrompts,
  setThreadId as setThreadIdInStore,
  initNewThread,
} from './ask-ai-store';
import {runPreSendCollectors} from 'src/components/agentic-ui/context-collectors';
import {publishAgenticEvent} from 'src/components/agentic-ui/agentic-ui-event-bus';
import {useHistory, useLocation} from 'src/rerouter';
import {useAgencyApiHeaders} from 'src/components/agentic-ui';
import {useAskAiPositioningContext} from './useAskAiPositioningContext';


type ThreadCreatePayload = {
  name: string;
  context: {
    page_context: {
      product: string;
    };
  };
};

export const AskAI = () => {
  const {t} = useLabelTranslation();
  const {
    isExtension,
    isClientIframe,
    isSenseAiIframe,
    isTrmIframe,
    hasContextInUrl,
  } = useEnvironmentContext();
  const queryClient = useQueryClient();
  const chatPanelRef = React.useRef<ChatPanelHandle | null>(null);
  const dispatch = useDispatch();
  const {headerSlot} = useRespondToAutoAttach(chatPanelRef);
  const isAuthed = useSelector(selectIsAuthed);
  const askAiEnabled = Boolean(useAgencyConfig('graceEnabled'));
  const product = useAskAIProduct();
  const threadId = useAskAIThreadId();
  const shouldFetchStarterPrompts = useShouldFetchStarterPrompts();
  const history = useHistory();
  const location = useLocation();
  const agencyHeaders = useAgencyApiHeaders();
  const isFullScreenChatExperience =
    location.query[FULLSCREEN_MODE_KEY] === 'true';
  const {elevation: askAiElevation, leftMargin} = useAskAiPositioningContext();

  const {data: threadData} = useApiQuery<{messages: ChatMessage[]}>(
    `conversation/${threadId}`,
    {
      queryOptions: {
        queryKey: threadId ? getMessageHistoryQueryKey(threadId) : skipToken,
        enabled: !!threadId,
      },
      apiOptions: {
        method: 'GET',
        options: {
          apiPath: '/grace/api/v1/',
          headers: agencyHeaders,
        },
      },
    },
  );
  const messageHistory = threadData?.messages || [];

  //if threadId is not there make an api call using useApiMutation to create a thread and set it in local storage
  const {mutateAsync: createThread, isPending: _isUpserting} = useApiMutation({
    mutationFn: async (payload: ThreadCreatePayload) => {
      return dispatch(
        reduxApiAskAi.post('conversation', payload, undefined, {
          headers: agencyHeaders,
        } as ApiPostOptions),
      );
    },
  });

  const handleCreateThread = async (): Promise<string> => {
    const thread = await createThread({
      name: 'Dummy thread name',
      context: {
        page_context: {
          product,
        },
      },
    });
    setThreadIdInStore(thread.id);
    return thread.id;
  };

  const handleClearThread = () => {
    // TODO:(diwakersurya)for now, default to workflow to get proper starter prompts
    // FIXME:(diwakersurya)it should remember the product from askai store.
    initNewThread({source: 'workflow', shouldFetchStarterPrompts: true});
    //initNewThread({source: product, shouldFetchStarterPrompts: true});
  };

  const getStreamUrl = async () => {
    const id = threadId ?? (await handleCreateThread());

    return `/grace/api/v1/conversation/${id}/chat`;
  };

  React.useEffect(() => {
    if (!chatPanelRef.current) {
      return;
    }
    if (isFullScreenChatExperience) {
      chatPanelRef.current.setMode('fullscreen', {persist: false});
      chatPanelRef.current.setOpen(true);
      return;
    }
    if (chatPanelRef.current.currentMode === 'fullscreen') {
      chatPanelRef.current.setMode('floating');
    }
  }, [isFullScreenChatExperience]);

  const handleSubmitFeedback = React.useCallback(
    async (
      messageId: string,
      feedback_rating: number,
      feedback_comment: string,
    ) => {
      await dispatch(
        reduxApiAskAi.patch(
          `conversation/message/${messageId}/feedback`,
          {rating: feedback_rating, comment: feedback_comment},
          undefined,
          {headers: agencyHeaders} as ApiPostOptions,
        ),
      );
      if (!threadId) {
        return;
      }
      // Update the query cache after submitting feedback so that we don't refetch
      queryClient.setQueryData(
        getMessageHistoryQueryKey(threadId),
        (oldData: {messages: ChatMessage[]}) => {
          if (!oldData || !oldData.messages) {
            return oldData;
          }
          return {
            ...oldData,
            messages: oldData.messages.map((message) =>
              message.id === messageId
                ? {...message, feedback_rating, feedback_comment}
                : message,
            ),
          };
        },
      );
      //also update the local streamed messages store
      if (chatPanelRef.current) {
        chatPanelRef.current.updateMessageFeedback(
          messageId,
          feedback_rating,
          feedback_comment,
        );
      }
    },
    [dispatch, agencyHeaders, threadId],
  );

  const handleClose = React.useCallback(() => {
    if (isFullScreenChatExperience) {
      const sourceurl = location.query.sourceurl ?? null;
      if (!sourceurl) {
        logger.error(
          'Source is required to exit out of fullscreen chat experience',
        );
        history.replace('/');
        return;
      }
      history.replace(sourceurl);
    }
  }, [history, isFullScreenChatExperience, location]);

  //NOTE:(diwakersurya) Do not show AskAI in extension, client iframe, or sense ai iframe.
  // hasContextInUrl is final if the url contains context, that means we are loading in a
  // separate iframe/environment other than app. we don't want to open sense ai there.
  if (
    !isAuthed ||
    !askAiEnabled ||
    isExtension ||
    isClientIframe ||
    isSenseAiIframe ||
    isTrmIframe ||
    hasContextInUrl
  ) {
    return null;
  }
  return (
    <>
      <ChatPanelLexical
        ref={(node) => {
          chatPanelRef.current = node;
          registerChatPanel(node);
        }}
        attachChatTo="#chat-container"
        attachHeaderTo="#chat-header-container"
        defaultMode={isFullScreenChatExperience ? 'fullscreen' : 'floating'}
        placeholder={t('Ask_me_anything', 'Ask me anything')}
        lastModePersistKey={LAST_MODE_STORAGE_KEY}
        positionPersistKey={POSITION_PERSIST_KEY}
        renderStartScreen={({publishMessageAction}) => {
          if (shouldFetchStarterPrompts) {
            return (
              <AgenticSuggestions
                publishMessageAction={
                  publishMessageAction as (action: ActionEvent) => void
                }
              />
            );
          }
          return null;
        }}
        messageHistory={messageHistory}
        onSubmitFeedback={handleSubmitFeedback}
        //we need this prop in attached mode to allow host to provide
        //custom content in the header slot
        headerSlot={headerSlot}
        height={400}
        chatOptions={{
          // resolve SSE URL lazily (creates thread on first send if needed)
          getStreamUrl,
          headers: {
            Authorization: `Bearer ${'test'}`,
            ...agencyHeaders,
          },
          collectMessageContext: () =>
            runPreSendCollectors({history}, undefined),
          publishMessageAction: (action: ActionEvent) => {
            try {
              const {product, event, payload, dedupeKey} = (action || {}) as {
                product: unknown;
                event: unknown;
                payload?: unknown;
                dedupeKey?: string;
              };
              if (event) {
                const emit = publishAgenticEvent as unknown as (
                  product: unknown,
                  event: unknown,
                  payload: unknown,
                  dedupeKey?: string,
                ) => void;
                emit(product, event, payload ?? null, dedupeKey);
              }
            } catch {}
          },
          backoff: {
            baseMs: 1000,
            maxMs: 15000,
            maxAttempts: 1,
          },
        }}
        defaultOpen={false}
        onReset={handleClearThread}
        panelLeftMargin={leftMargin}
        elevation={askAiElevation}
        onClose={handleClose}
      />
    </>
  );
};

export interface AskAIChatHostProps {
  className?: string;
}

export const AskAIChatHost = ({className}: AskAIChatHostProps) => {
  useRequestToAutoAttach();
  return (
    <div
      id="chat-container"
      className={classify(css.panelWrapper, className)}
    ></div>
  );
};
export interface AskAIHeaderHostProps {
  className?: string;
  slot?: React.ReactNode;
}

export const AskAIHeaderHost = ({className, slot}: AskAIHeaderHostProps) => {
  useHeaderSlot(slot);
  return (
    <div
      id="chat-header-container"
      className={classify(css.headerWrapper, className)}
    ></div>
  );
};
